import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, map, shareReplay, takeUntil} from 'rxjs/operators';
import {
    APIResponse,
    Filter,
    FilterFieldOptionRequest,
    FiltersDynamicOptionsStore,
    TableFilterConfig,
} from '@core/interfaces/system/system-common';
import {AssetRegistryService} from '@core/interfaces/asset-registry';
import {UsersStore} from '@store/common/users.store';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {AssetColumn} from '@core/interfaces/common/asset';
import {isEqual} from 'lodash';
import {MaintenancePlanningService} from '@core/interfaces/engin/maintenance-planning/maintenance-planning';

@Injectable()
export class MaintenancePlanningStore extends Unsubscribable {
    public resultsLoading = new BehaviorSubject<boolean>(false);
    readonly resultsLoading$ = this.resultsLoading.asObservable();

    // Generic filtering
    public selectedFilters = new BehaviorSubject<Filter[]>([]);
    public selectedFilters$ = this.selectedFilters.asObservable();

    public genericFilters$ = this.maintenancePlanningService.getFilterOptions().pipe(
        map((res: APIResponse<TableFilterConfig[]>) => res.response),
        shareReplay(1),
    );

    public filtersDynamicOptions: BehaviorSubject<FiltersDynamicOptionsStore> = new BehaviorSubject<any>([]);
    public filtersDynamicOptions$: Observable<FiltersDynamicOptionsStore> = this.filtersDynamicOptions
        .asObservable()
        .pipe(shareReplay(1));

    // Inspection Dashboard, group options and selected
    public selectedGroup = new BehaviorSubject<AssetColumn>(null);
    public selectedGroup$ = this.selectedGroup.asObservable();

    public availableGroups = new BehaviorSubject<AssetColumn[]>([]);
    public availableGroups$ = this.availableGroups.asObservable().pipe(shareReplay(1));

    constructor(private maintenancePlanningService: MaintenancePlanningService, private userStore: UsersStore) {
        super();

        this.setInitialFiltersFromLocalStorage();
        this.getAvailableGroups();
    }

    private setInitialFiltersFromLocalStorage() {
        this.userStore.currentUser$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((user) => !!user),
            )
            .subscribe((currUser) => {
                let _key = `selectedFiltersMaintenancePlanning_${currUser.id}`;
                let _filtersJSON = localStorage.getItem(_key);
                if (_filtersJSON) this.selectedFilters.next(JSON.parse(_filtersJSON));
            });
    }

    public setSelectedFilters(filters: Filter[], requiredChange = false) {
        if (!requiredChange && isEqual(filters, this.selectedFilters.value)) return;
        this.selectedFilters.next(filters);

        let _key = `selectedFiltersMaintenancePlanning_${this.userStore.getCurrentUser().id}`;
        localStorage.setItem(_key, JSON.stringify(filters));
    }

    private setFiltersDynamicOptions(value: FiltersDynamicOptionsStore) {
        return this.filtersDynamicOptions.next(value);
    }

    public getOptionsDynamically(data: FilterFieldOptionRequest) {
        this.setFiltersDynamicOptions({
            ...this.filtersDynamicOptions.value,
            [data.fieldKey]: {
                scope: data.filterParams,
                options: this.maintenancePlanningService.getFilterFieldOptions(data).pipe(
                    map((res) => res.response?.data),
                    shareReplay(1),
                ),
            },
        });
    }

    private getAvailableGroups() {
        this.maintenancePlanningService
            .getGroupOptions()
            .pipe(map((res: APIResponse<AssetColumn[]>) => res.response))
            .subscribe((columns: AssetColumn[]) => {
                this.availableGroups.next(columns);
                // Set default option
                if (columns && columns.length >= 1) {
                    this.setSelectedGroup(columns[0]);
                }
            });
    }

    public setSelectedGroup(group: AssetColumn, requiredChange = false) {
        if (!requiredChange && isEqual(group, this.selectedGroup.value)) return;
        this.selectedGroup.next(group);
    }

    public setSelectedGroupByKey(groupKey: string, requiredChange = false) {
        if (!requiredChange && isEqual(groupKey, this.selectedGroup.value.fieldKey)) return;
        const group: AssetColumn = this.availableGroups.value.find((g: AssetColumn) => g.fieldKey == groupKey);
        this.selectedGroup.next(group);
    }
}
